import axios from "axios";
import { API_CONFIG } from "../utils/apiConfig";
import { normalizeApiError } from "../utils/apiError";
import {
  clearStoredAuthSession,
  getStoredAuthSession,
  setStoredAuthSession,
} from "../utils/storage";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "../utils/token";
import {
  clearActivity,
  isInactivityExceeded,
  markActivity,
} from "../utils/sessionActivity";

const apiClient = axios.create({
  baseURL: API_CONFIG.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

const refreshClient = axios.create({
  baseURL: API_CONFIG.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    markActivity();
  }

  return config;
});

let isRefreshing = false;
let queuedRequests = [];

function processQueue(error, token = null) {
  queuedRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  queuedRequests = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const normalizedError = normalizeApiError(error);
    const hasRefreshToken = Boolean(getRefreshToken());
    const isRefreshCall = originalRequest?.url?.includes("/auth/refresh");

    if (
      normalizedError.status === 401 &&
      hasRefreshToken &&
      !isRefreshCall &&
      !originalRequest?._retry
    ) {
      if (isInactivityExceeded()) {
        clearTokens();
        clearStoredAuthSession();
        clearActivity();
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(normalizedError);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queuedRequests.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await refreshClient.post("/auth/refresh", {
          refreshToken: getRefreshToken(),
        });
        const refreshedData = refreshResponse?.data?.data || refreshResponse?.data || {};
        const tokens = refreshedData?.tokens || refreshedData;
        const newAccessToken = tokens?.accessToken;
        const newRefreshToken = tokens?.refreshToken;
        if (newAccessToken) setAccessToken(newAccessToken);
        if (newRefreshToken) setRefreshToken(newRefreshToken);
        const currentSession = getStoredAuthSession();
        if (currentSession && newAccessToken) {
          setStoredAuthSession({
            ...currentSession,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken || currentSession.refreshToken,
          });
        }
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        clearStoredAuthSession();
        clearActivity();
        
        // Force logout and redirect to login page
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        
        return Promise.reject(normalizeApiError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    if (normalizedError.status === 401) {
      clearTokens();
      clearStoredAuthSession();
      clearActivity();
      
      // Force logout and redirect to login page
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(normalizedError);
  }
);

export default apiClient;
